<template>
  <b-modal
    :title="
      viewingDocument
        ? formatDocumentDateTime(documentModalDate(viewingDocument))
        : ''
    "
    id="document-view-modal"
    ref="document-view-modal"
    size="lg"
    hide-footer
    modal-class="dark-modal dark-modal--light"
    @hidden="close"
  >
    <div v-if="viewingDocument" class="doc-modal__body">
      <template v-if="viewingDocument._type === 'receita'">
        <p class="doc-modal__meta">
          Paciente: {{ viewingDocument.patient_name || "—" }}
        </p>
        <p class="doc-modal__meta">
          Profissional de saúde: {{ viewingDocument.doctor_name || "—" }}
        </p>
        <p class="doc-modal__meta">
          LME: {{ viewingDocument.lme ? "Sim" : "Não" }}
        </p>
        <p>Medicamentos:</p>
        <ul
          v-if="viewingDocument.drugs && viewingDocument.drugs.length"
          class="doc-modal__drugs"
        >
          <li
            v-for="drug in viewingDocument.drugs"
            :key="drug.id"
            class="doc-modal__drug"
          >
            <strong>{{ drug.name }}</strong>
            <span v-if="drug.sanitized_posology">
              — {{ drug.sanitized_posology }}
            </span>
          </li>
        </ul>
        <img
          v-if="isImageDocument(viewingDocument, viewingDocument.links)"
          :src="viewingDocument.links"
          class="doc-modal__image"
          alt="Receita médica"
          @error="handleImageError('links')"
        />
        <button
          v-else-if="viewingDocument.links"
          type="button"
          class="doc-modal__file-link"
          @click="openInNewTab('links')"
        >
          Abrir arquivo anexado
        </button>
        <p v-else class="doc-modal__text">Arquivo da receita indisponível.</p>
      </template>

      <template v-else-if="viewingDocument._type === 'atestado'">
        <div class="doc-modal__meta-row">
          <span class="doc-modal__meta">
            Dias de afastamento: {{ viewingDocument.days_off || "—" }}
          </span>
          <span class="doc-modal__meta">
            CID: {{ viewingDocument.cid_code || "—" }}
          </span>
        </div>

        <div class="doc-modal__section">
          <p class="doc-modal__section-title">Observações</p>
          <p class="doc-modal__text">
            {{ trimmedText(viewingDocument.description) || "Sem descrição" }}
          </p>
        </div>

        <div class="doc-modal__section">
          <p class="doc-modal__section-title">Arquivo</p>
          <div
            v-if="isImageDocument(viewingDocument, viewingDocument.file_url)"
            class="doc-modal__image-wrap"
            @click="openInNewTab('file_url')"
          >
            <img
              :src="viewingDocument.file_url"
              alt="Atestado anexado"
              class="doc-modal__image"
              @error="handleImageError('file_url')"
            />
            <div class="doc-modal__image-overlay">
              Clique aqui para ampliar a imagem em uma nova aba
            </div>
          </div>
          <button
            v-else-if="viewingDocument.file_url"
            type="button"
            class="doc-modal__file-link"
            @click="openInNewTab('file_url')"
          >
            Abrir arquivo anexado
          </button>
          <p v-else class="doc-modal__text doc-modal__text--muted">
            Arquivo do atestado indisponível.
          </p>
        </div>
      </template>

      <template v-else>
        <div
          v-if="isImageDocument(viewingDocument, viewingDocument.file_url)"
          class="doc-modal__image-wrap"
          @click="openInNewTab('file_url')"
        >
          <img
            :src="viewingDocument.file_url"
            alt="Exame anexado"
            class="doc-modal__image"
            @error="handleImageError('file_url')"
          />
          <div class="doc-modal__image-overlay">
            Clique aqui para ampliar a imagem em uma nova aba
          </div>
        </div>
        <button
          v-else-if="viewingDocument.file_url"
          type="button"
          class="doc-modal__file-link"
          @click="openInNewTab('file_url')"
        >
          Abrir arquivo anexado
        </button>
        <p class="doc-modal__text">
          {{ viewingDocument.description || "Sem descrição" }}
        </p>
      </template>
    </div>
  </b-modal>
</template>

<script>
import { parseISO, format } from "date-fns";
import { pt as locale } from "date-fns/locale";

export default {
  name: "DocumentViewModal",
  data() {
    return {
      viewingDocument: null,
      refresh: null,
      imageRefreshAttempted: false
    };
  },
  methods: {
    // `refresh` é opcional: uma função assíncrona que busca o documento de novo
    // e devolve uma versão com URLs recém-geradas. As URLs assinadas do storage
    // expiram poucos minutos após serem geradas — se o modal ficar aberto além
    // desse tempo, a URL capturada no momento do open() já não serve mais.
    open(doc, refresh = null) {
      this.viewingDocument = doc;
      this.refresh = refresh;
      this.imageRefreshAttempted = false;
      this.$bvModal.show("document-view-modal");
    },

    close() {
      this.viewingDocument = null;
      this.refresh = null;
      this.imageRefreshAttempted = false;
    },

    // A <img> renderiza direto a URL assinada capturada na listagem — se ela
    // já tiver expirado (ou expirar com o modal aberto), o carregamento falha
    // e o navegador dispara "error". Só então vale a pena buscar uma URL nova;
    // só tenta uma vez por abertura pra não entrar em loop se a nova também falhar.
    async handleImageError(key) {
      if (!this.refresh || this.imageRefreshAttempted) return;
      this.imageRefreshAttempted = true;

      try {
        const fresh = await this.refresh();
        if (fresh && fresh[key]) {
          this.viewingDocument = { ...this.viewingDocument, ...fresh };
        }
      } catch {
        // sem mais o que fazer — a imagem segue quebrada
      }
    },

    async openInNewTab(key) {
      // Abre a aba antes do await pra não perder o gesto do usuário (popup blocker).
      const newTab = window.open("", "_blank");
      if (newTab) newTab.document.write("Carregando arquivo…");

      let url = this.viewingDocument[key];

      if (this.refresh) {
        try {
          const fresh = await this.refresh();
          if (fresh && fresh[key]) {
            this.viewingDocument = { ...this.viewingDocument, ...fresh };
            url = fresh[key];
          }
        } catch {
          // segue com a URL que já tínhamos como último recurso
        }
      }

      if (newTab) newTab.location = url;
    },

    formatDocumentDateTime(value) {
      if (!value) return "";
      return format(parseISO(value), "dd/MM/yyyy · HH:mm", { locale });
    },

    documentModalDate(doc) {
      if (!doc) return null;
      if (doc._type === "receita")
        return doc.prescription_date || doc.created_at;
      return doc.created_at;
    },

    isImageUrl(url) {
      return !!url && /\.(jpe?g|png|gif|webp)(\?.*)?$/i.test(url);
    },

    isImageDocument(doc, url) {
      if (doc.content_type) return doc.content_type.startsWith("image/");
      return this.isImageUrl(url);
    },

    trimmedText(value) {
      if (!value) return "";
      return value.trim().replace(/\n{3,}/g, "\n\n");
    }
  }
};
</script>

<style scoped>
.doc-modal__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: auto;
  min-height: 120px;
  max-height: 560px;
}

.doc-modal__image-wrap {
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
}

.doc-modal__image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: var(--space-4, 16px);
  background: rgba(0, 0, 0, 0.55);
  /* `.dark-modal--light .modal-body div` (global, _patient-record.scss) tem
     mais especificidade que essa regra scoped e forçava um cinza escuro aqui
     — ilegível sobre o fundo escuro do overlay. */
  color: #fff !important;
  font-size: var(--font-size-sm, 14px);
  font-weight: var(--fw-semibold, 600);
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}

.doc-modal__image-wrap:hover .doc-modal__image-overlay {
  opacity: 1;
}

.doc-modal__image-wrap:hover .doc-modal__image {
  opacity: 0.85;
}

.doc-modal__image {
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: contain;
  border-radius: 8px;
  background: var(--color-gray-100, #f3f4f6);
  transition: opacity 0.15s;
}

.doc-modal__file-link {
  align-self: flex-start;
  font-size: var(--font-size-sm, 14px);
  font-weight: var(--fw-semibold, 600);
  color: var(--color-primary, #1a6cf7);
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.doc-modal__text {
  font-size: var(--font-size-sm, 14px);
  line-height: 1.5;
  white-space: pre-wrap;
  margin: 0;
}

.doc-modal__image + .doc-modal__text,
.doc-modal__image-wrap + .doc-modal__text {
  margin-top: var(--space-2, 8px);
}

.doc-modal__meta {
  font-size: var(--font-size-sm, 14px);
  color: var(--color-gray-600, #4b5563);
  margin: 0;
}

.doc-modal__meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
}

.doc-modal__section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.doc-modal__section-title {
  font-size: var(--font-size-xs, 12px);
  font-weight: var(--fw-semibold, 600);
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--color-gray-500, #6b7280);
  margin: 0;
}

.doc-modal__text--muted {
  color: var(--color-gray-400, #9ca3af);
  font-style: italic;
}

.doc-modal__drugs {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.doc-modal__drug {
  font-size: var(--font-size-sm, 14px);
  line-height: 1.5;
}
</style>
